/**
 * 気象データの1分tickへの補間と派生量（§4.3・§4.4）。
 * Open-Meteoの1時間値を線形補間で1分へ落とす。UVIの日変化は滑らかなため十分。
 */
import { FD_UV_MAX, FD_UV_MIN, FD_UV_OFFSET, FD_UV_SLOPE } from "./constants.js";
import type { Ms, WxHourly, WxMinute } from "./types.js";

export function clip(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** 広帯域散乱比 → UV散乱比の補正写像（§4.3）【係数は要校正】 */
export function fdUv(fdBb: number): number {
  return clip(FD_UV_OFFSET + FD_UV_SLOPE * fdBb, FD_UV_MIN, FD_UV_MAX);
}

/** 雲補正比 CMF = UVI/UVIc。夜間（UVIc=0）は0と定義（§4.2） */
export function cmf(uvi: number, uviClearSky: number): number {
  return uviClearSky > 0 ? uvi / uviClearSky : 0;
}

function lerp(a: number, b: number, f: number): number {
  return a + (b - a) * f;
}

/**
 * 1時間値の系列から時刻tsの1分値を線形補間する。
 * 範囲外は端の値を使う（stale延命、§6.3）。
 */
export function interpolateWx(hourly: WxHourly, ts: Ms, degraded = false): WxMinute {
  const times = hourly.time.map((t) => Date.parse(t));
  const n = times.length;
  if (n === 0) throw new Error("wx: empty hourly series");

  let i = 0;
  while (i < n - 1 && times[i + 1]! <= ts) i++;
  const t0 = times[i]!;
  const t1 = i < n - 1 ? times[i + 1]! : t0;
  const f = t1 > t0 ? clip((ts - t0) / (t1 - t0), 0, 1) : 0;
  const j = Math.min(i + 1, n - 1);

  const at = (arr: number[]): number => lerp(arr[i] ?? 0, arr[j] ?? arr[i] ?? 0, f);

  const uvi = Math.max(0, at(hourly.uv_index));
  const uviClearSky = Math.max(0, at(hourly.uv_index_clear_sky));
  const direct = Math.max(0, at(hourly.direct_radiation));
  const diffuse = Math.max(0, at(hourly.diffuse_radiation));
  // 日射ゼロ（夜間・欠測）は全散乱扱い。日陰でも被曝を数える側＝安全側
  const fdBb = direct + diffuse > 0 ? diffuse / (direct + diffuse) : 1;

  return {
    uvi,
    uviClearSky,
    fdBb,
    cmf: cmf(uvi, uviClearSky),
    tempC: at(hourly.temperature_2m),
    rh: at(hourly.relative_humidity_2m),
    pm25: hourly.pm2_5 ? Math.max(0, at(hourly.pm2_5)) : 0,
    degraded,
  };
}
